import React from 'react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import ProductCard from './ProductCard';
import ProductCardSkeleton from './ProductCardSkeleton';
import { Product } from '@/services/apis/products';
import { useTranslation } from '@/hooks/useTranslation';

interface ProductsGridProps {
  products: Product[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onShowDetails: (product: Product) => void;
}

const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onShowDetails,
}) => {
  const { t } = useTranslation('products');
  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  if (isLoading && products.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} onShowDetails={onShowDetails} />
        ))}
      </div>

      {/* Infinite Scroll Loader */}
      {hasNextPage && (
        <div ref={loaderRef} className="mt-12">
          {isFetchingNextPage && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <ProductCardSkeleton key={`skeleton-${i}`} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* End of Results */}
      {!hasNextPage && products.length > 0 && (
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-full border border-gray-200/50 dark:border-gray-800/50 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t('states.endOfResults')}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsGrid;

